<template>
  <div class="user-manage-container">
    <div class="page-header">
      <div>
        <h2>用户数据</h2>
        <p>检索用户账号、OpenID、权益余额和剩余服务次数</p>
      </div>
    </div>

    <el-card class="box-card table-card">
      <!-- 搜索栏 -->
      <div class="search-wrapper">
        <el-input
          v-model="keyword"
          placeholder="请输入关键词搜索（用户名、手机号、OpenID等）"
          clearable
          class="search-input"
          @keyup.enter="handleSearch"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-button type="primary" :icon="Search" @click="handleSearch">
          搜索
        </el-button>
        <el-button :icon="Refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 用户列表表格 -->
      <el-table
        v-loading="loading"
        :data="userList"
        stripe
        style="width: 100%"
        :empty-text="emptyText"
      >
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="_id" label="用户 ID" min-width="120" />
        <el-table-column prop="username" label="用户名" min-width="120" />
        <el-table-column prop="openid" label="微信OpenID" min-width="120" />
        <el-table-column
          prop="createdAt"
          label="创建时间"
          width="120"
          align="center"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="wwCoinBalance"
          label="旺旺币余额"
          width="120"
          align="center"
        >
          <template #default="{ row }">
            {{
              row.wwCoinBalance !== undefined
                ? row.wwCoinBalance.toFixed(2)
                : '0.00'
            }}
          </template>
        </el-table-column>
        <el-table-column
          prop="resumeRemainingCount"
          label="剩余押题"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ row.resumeRemainingCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="specialRemainingCount"
          label="剩余专项"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ row.specialRemainingCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column
          prop="behaviorRemainingCount"
          label="剩余HR"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            {{ row.behaviorRemainingCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              size="small"
              @click="handleViewDetail(row)"
            >
              详情
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-wrapper">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { getUserList } from '@/api/user'
import { ref, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh } from '@element-plus/icons-vue'
import { dateFormat } from '@/utils/date'
import { useRouter } from 'vue-router'

const router = useRouter()

const userList = ref([])
const total = ref(0)
const page = ref(1)
const limit = ref(20)
const keyword = ref('')
const loading = ref(false)
const emptyText = ref('暂无数据')

/**
 * 格式化日期
 */
const formatDate = (date) => {
  if (!date) return '-'
  const timestamp = new Date(date).getTime()
  return dateFormat(timestamp, 'YYYY-MM-DD HH:mm:ss')
}

/**
 * 获取用户列表数据
 */
const getUserListData = async () => {
  loading.value = true
  emptyText.value = '加载中...'
  try {
    const res = await getUserList(page.value, limit.value, keyword.value)
    userList.value = res.data || []
    total.value = res.total || 0
    emptyText.value = '暂无数据'
  } catch (error) {
    console.error('获取用户列表失败:', error)
    userList.value = []
    total.value = 0
    emptyText.value = '加载失败，请重试'
    ElMessage.error('获取用户列表失败')
  } finally {
    loading.value = false
  }
}

/**
 * 搜索处理
 */
const handleSearch = () => {
  page.value = 1
  getUserListData()
}

/**
 * 重置搜索
 */
const handleReset = () => {
  keyword.value = ''
  page.value = 1
  getUserListData()
}

/**
 * 分页大小改变
 */
const handleSizeChange = (val) => {
  limit.value = val
  page.value = 1
  getUserListData()
}

/**
 * 当前页改变
 */
const handleCurrentChange = (val) => {
  page.value = val
  getUserListData()
}

/**
 * 查看用户详情
 */
const handleViewDetail = (row) => {
  router.push({
    path: '/user/detail',
    query: { id: row._id }
  })
}

// 初始化加载数据
onMounted(() => {
  getUserListData()
})
</script>

<style scoped lang="scss">
.user-manage-container {
  .box-card {
    .search-wrapper {
      .search-input {
        flex: 1;
        max-width: 400px;
      }
    }
  }
}

:deep(.el-table) {
  .el-table__header {
    th {
      font-weight: 600;
    }
  }
}
</style>
